import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PlusIcon, EditIcon, Trash2Icon, UsersIcon, Loader2Icon, Users2Icon } from 'lucide-react';
import { AddTeamDialog, EditTeamDialog } from '@/components/teams';
import { Empty, EmptyHeader, EmptyMedia, EmptyTitle, EmptyDescription, EmptyContent } from '@/components/ui/empty';
import { useTeams, useCreateTeam, useUpdateTeam, useDeleteTeam } from '@/lib/hooks/useTeams';
import type { Team } from '@repo/types';
import type { TeamFormData } from '@/components/teams/AddTeamDialog';
import type { TeamUpdateData } from '@/components/teams/EditTeamDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export const Route = createFileRoute('/admin/settings')({
  component: SettingsPage,
});

function SettingsPage() {
  const [addTeamDialogOpen, setAddTeamDialogOpen] = useState(false);
  const [editTeamDialogOpen, setEditTeamDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);

  // API hooks
  const { data: teamsData, isLoading, error } = useTeams(1, 100);
  const createTeamMutation = useCreateTeam();
  const updateTeamMutation = useUpdateTeam();
  const deleteTeamMutation = useDeleteTeam();

  const teams = teamsData?.data || [];

  const handleAddTeam = async (data: TeamFormData) => {
    await createTeamMutation.mutateAsync({
      name: data.name,
      leadId: data.leadId || undefined,
    });
    setAddTeamDialogOpen(false);
  };

  const handleEditTeam = async (data: TeamUpdateData) => {
    if (!selectedTeam) return;

    await updateTeamMutation.mutateAsync({
      id: selectedTeam.id,
      data: {
        name: data.name,
        leadId: data.leadId,
      },
    });
    setEditTeamDialogOpen(false);
    setSelectedTeam(null);
  };

  const handleDeleteTeam = (team: Team) => {
    setSelectedTeam(team);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteTeam = async () => {
    if (!selectedTeam) return;

    await deleteTeamMutation.mutateAsync(selectedTeam.id);
    setDeleteDialogOpen(false);
    setSelectedTeam(null);
  };

  const handleEditClick = (team: Team) => {
    setSelectedTeam(team);
    setEditTeamDialogOpen(true);
  };

  return (
    <div className="flex flex-col gap-6 p-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground">
          Manage system configuration and team structure
        </p>
      </div>

      <Tabs defaultValue="teams" className="w-full">
        <TabsList>
          <TabsTrigger value="teams">Teams</TabsTrigger>
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
        </TabsList>

        <TabsContent value="teams" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Team Management</CardTitle>
                  <CardDescription>
                    Create and manage teams for employee organization
                  </CardDescription>
                </div>
                <Button onClick={() => setAddTeamDialogOpen(true)}>
                  <PlusIcon className="mr-2 h-4 w-4" />
                  Add Team
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {isLoading && (
                <div className="flex items-center justify-center py-8">
                  <Loader2Icon className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              )}

              {error && (
                <div className="text-center py-8 text-destructive">
                  Failed to load teams. Please try again.
                </div>
              )}

              {!isLoading && !error && teams.length === 0 && (
                <Empty>
                  <EmptyHeader>
                    <EmptyMedia variant="icon">
                      <Users2Icon className="h-6 w-6 text-muted-foreground" />
                    </EmptyMedia>
                    <EmptyTitle>No teams yet</EmptyTitle>
                    <EmptyDescription>
                      Get started by creating your first team to organize employees and manage team structures.
                    </EmptyDescription>
                  </EmptyHeader>
                  <EmptyContent>
                    <Button onClick={() => setAddTeamDialogOpen(true)}>
                      <PlusIcon className="mr-2 h-4 w-4" />
                      Create Your First Team
                    </Button>
                  </EmptyContent>
                </Empty>
              )}

              {!isLoading && !error && teams.length > 0 && (
                <div className="space-y-4">
                  {teams.map((team) => (
                    <div
                      key={team.id}
                      className="flex items-center justify-between rounded-lg border p-4 hover:bg-muted/50 transition-colors"
                    >
                      <div className="space-y-2">
                        <div className="flex items-center gap-3">
                          <h3 className="font-semibold text-lg">{team.name}</h3>
                          <Badge variant="secondary" className="gap-1">
                            <UsersIcon className="h-3 w-3" />
                            {team.memberCount} members
                          </Badge>
                        </div>
                        <div className="flex flex-col gap-1 text-sm text-muted-foreground">
                          <p>Team Lead: {team.leadName || 'No lead assigned'}</p>
                          <p>Created: {new Date(team.createdAt).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => handleEditClick(team)}>
                          <EditIcon className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleDeleteTeam(team)}
                        >
                          <Trash2Icon className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="general" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>General Settings</CardTitle>
              <CardDescription>Platform configuration and preferences</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">General settings coming soon...</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Notification Settings</CardTitle>
              <CardDescription>Configure email and push notifications</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">Notification settings coming soon...</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <AddTeamDialog
        open={addTeamDialogOpen}
        onOpenChange={setAddTeamDialogOpen}
        onSubmit={handleAddTeam}
      />

      {selectedTeam && (
        <EditTeamDialog
          open={editTeamDialogOpen}
          onOpenChange={setEditTeamDialogOpen}
          onSubmit={handleEditTeam}
          team={selectedTeam}
        />
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Team</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedTeam?.name}"? This will remove all team
              assignments from members. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteTeam}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
